/**
 * PMS Storage Module
 *
 * Wraps all Supabase Storage operations so the raw client
 * is never imported outside `src/integrations/pms/`.
 */

import { supabase } from '@/integrations/supabase/client';

export interface UploadOptions {
  cacheControl?: string;
  upsert?: boolean;
  contentType?: string;
}

export interface UploadResult {
  path: string;
  publicUrl: string;
}

export const pmsStorage = {
  /**
   * Upload a file to a storage bucket and return its public URL.
   */
  upload: async (
    bucket: string,
    path: string,
    file: File | Blob,
    options?: UploadOptions,
  ): Promise<UploadResult> => {
    const { error } = await supabase.storage
      .from(bucket)
      .upload(path, file, {
        cacheControl: options?.cacheControl ?? '3600',
        upsert: options?.upsert ?? false,
        contentType: options?.contentType,
      });
    if (error) throw error;

    const { data: urlData } = supabase.storage
      .from(bucket)
      .getPublicUrl(path);

    return { path, publicUrl: urlData.publicUrl };
  },

  /**
   * Get the public URL for a file already in a bucket.
   */
  getPublicUrl: (bucket: string, path: string): string => {
    const { data } = supabase.storage.from(bucket).getPublicUrl(path);
    return data.publicUrl;
  },

  /**
   * Delete one or more files from a bucket.
   */
  remove: async (bucket: string, paths: string[]): Promise<void> => {
    const { error } = await supabase.storage.from(bucket).remove(paths);
    if (error) throw error;
  },

  /**
   * List files in a bucket path.
   */
  list: async (bucket: string, path?: string) => {
    const { data, error } = await supabase.storage.from(bucket).list(path);
    if (error) throw error;
    return data;
  },
};
